import { z } from 'zod';
import { UuidSchema } from '../_shared/ids.js';
import { reconocerEmbed } from './embeds.js';
import { PublicImage } from './public-image.js';
/**
 * Cuerpo enriquecido de una nota: un documento **en JSON**, con la forma que produce el
 * editor del panel (TipTap/ProseMirror) y que se guarda tal cual en `Article.body`.
 *
 * Dos decisiones que sostienen todo lo demás:
 *
 *  - **JSON y no HTML.** El sitio público va a recorrer estos nodos y renderizar componentes;
 *    nunca va a inyectar una cadena de marcado ajena. Un cuerpo malicioso no tiene por dónde
 *    entrar: lo que no está en esta lista blanca no se guarda.
 *  - **Lista blanca cerrada** (`.strict()` en cada nodo). Si mañana el editor suma un bloque,
 *    hay que agregarlo acá a propósito — y el sitio público se enterará de que existe.
 *  - **Ninguna dirección de imagen se guarda.** El nodo `image` referencia una media por id y la
 *    URL se resuelve al leer. Ver el comentario de `ArticleImageNode`.
 *
 * Los nodos son exactamente los que dibujó el diseño en la barra de herramientas —negrita,
 * itálica, tachado, H2, H3, lista, cita, enlace, imagen y video— más la publicación de una red
 * embebida, que se sumó después.
 */
/** Solo `http`/`https`: un `javascript:` en un enlace del cuerpo sería un XSS en el sitio. */
const HrefSchema = z
    .string()
    .url()
    .refine((u) => /^https?:\/\//i.test(u), 'solo enlaces http o https');
const MarkSchema = z.union([
    z.object({ type: z.literal('bold') }).strict(),
    z.object({ type: z.literal('italic') }).strict(),
    z.object({ type: z.literal('strike') }).strict(),
    z
        .object({
        type: z.literal('link'),
        attrs: z
            .object({
            href: HrefSchema,
            target: z.enum(['_blank', '_self']).nullish(),
            rel: z.string().nullish(),
            class: z.string().nullish(),
        })
            .strict(),
    })
        .strict(),
]);
const TextNodeSchema = z
    .object({
    type: z.literal('text'),
    text: z.string().min(1),
    marks: z.array(MarkSchema).optional(),
})
    .strict();
const HardBreakSchema = z.object({ type: z.literal('hardBreak') }).strict();
const InlineSchema = z.union([TextNodeSchema, HardBreakSchema]);
const ParagraphSchema = z
    .object({
    type: z.literal('paragraph'),
    content: z.array(InlineSchema).optional(),
})
    .strict();
/** Solo H2 y H3: el H1 de la página es el título de la nota. */
const HeadingSchema = z
    .object({
    type: z.literal('heading'),
    attrs: z.object({ level: z.union([z.literal(2), z.literal(3)]) }).strict(),
    content: z.array(InlineSchema).optional(),
})
    .strict();
const ListItemSchema = z.lazy(() => z
    .object({
    type: z.literal('listItem'),
    content: z.array(BlockSchema).min(1),
})
    .strict());
const BulletListSchema = z.lazy(() => z
    .object({
    type: z.literal('bulletList'),
    content: z.array(ListItemSchema).min(1),
})
    .strict());
const BlockquoteSchema = z.lazy(() => z
    .object({
    type: z.literal('blockquote'),
    content: z.array(BlockSchema).min(1),
})
    .strict());
/**
 * Imagen del cuerpo. `alt` es obligatorio y no vacío **en el propio esquema**: una foto sin
 * texto alternativo no llega a guardarse, no hace falta validarlo aparte.
 *
 * **No hay `src`, y eso es la mitad del punto.** El cuerpo guarda una *referencia* a la media
 * (`mediaId`), no una dirección: una URL guardada acá ata el contenido al dominio del día en que
 * se insertó la foto, y cambiar de dominio deja las imágenes rotas dentro del JSON de cada nota.
 * Ya pasó: los artículos de la demo tenían `http://localhost:9000/...` incrustado y el sitio
 * público pedía las fotos a `localhost`. Sin campo donde ponerla, no puede volver a pasar.
 *
 * `imagen` es la contracara: la API la **resuelve al leer** (`articleBodyConImagenes`) para que
 * el sitio y el editor tengan qué dibujar. Nunca se persiste — el servidor la saca en cada
 * escritura (`articleBodySinImagenes`), así que da lo mismo si el editor la manda de vuelta.
 */
export const ArticleImageNode = z
    .object({
    type: z.literal('image'),
    attrs: z
        .object({
        mediaId: UuidSchema,
        alt: z.string().trim().min(1, 'la imagen necesita texto alternativo'),
        caption: z.string().trim().max(300).nullish(),
        fullWidth: z.boolean().default(false),
        /** Resuelta al leer, descartada al escribir. Ver el comentario de arriba. */
        imagen: PublicImage.nullish(),
    })
        .strict(),
})
    .strict();
/** Video embebido. Hoy solo YouTube (es lo que definió el modelo de datos). */
export const ArticleVideoNode = z
    .object({
    type: z.literal('video'),
    attrs: z
        .object({
        provider: z.literal('youtube'),
        /** Id del video, no la URL: la URL la arma el que renderiza. */
        videoId: z.string().regex(/^[A-Za-z0-9_-]{11}$/, 'id de video de YouTube inválido'),
        caption: z.string().trim().max(300).nullish(),
    })
        .strict(),
})
    .strict();
/**
 * Publicación de una red embebida (Instagram, X, Facebook, TikTok).
 *
 * Guarda el **id** y no el marcado, por lo mismo que el video: la dirección del iframe la arma el
 * que renderiza. El HTML que devuelven los oEmbed de las redes trae un `<script>` adentro, y
 * guardarlo obligaría al sitio a inyectar marcado ajeno — justo lo que este esquema existe para
 * impedir.
 *
 * **Validar es reconocer.** En vez de repetir acá las reglas de cada red, se le pregunta a
 * `reconocerEmbed()`, que es lo mismo que usa el editor para entender el link pegado. Así no hay
 * dos juegos de reglas que puedan desincronizarse: lo que el contrato acepta es, por construcción,
 * exactamente lo que el panel sabe producir. De paso obliga a que la dirección guardada sea la
 * **canónica** que arma el reconocedor, así que un link con parámetros de seguimiento pegados no
 * entra ni aunque alguien escriba el JSON a mano.
 *
 * `postId` es `null` sólo en Facebook: no tiene un id portable y su plugin recibe la dirección
 * entera. Ahí la validación de la URL es la única guarda que hay.
 */
export const ArticleEmbedNode = z
    .object({
    type: z.literal('embed'),
    attrs: z
        .object({
        provider: z.enum(['instagram', 'x', 'facebook', 'tiktok']),
        postId: z.string().nullable(),
        url: HrefSchema,
        caption: z.string().trim().max(300).nullish(),
    })
        .strict()
        .superRefine((attrs, ctx) => {
        const reconocido = reconocerEmbed(attrs.url);
        if (!reconocido.ok) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['url'],
                message: 'no es la dirección de una publicación de una red',
            });
            return;
        }
        const { embed } = reconocido;
        if (embed.provider !== attrs.provider) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['url'],
                message: `la dirección es de ${embed.provider}, no de ${attrs.provider}`,
            });
        }
        if (embed.url !== attrs.url) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['url'],
                message: 'la dirección tiene que ser la canónica de la publicación',
            });
        }
        if (embed.postId !== attrs.postId) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['postId'],
                message: 'el id no es el de la publicación de esa dirección',
            });
        }
    }),
})
    .strict();
const BlockSchema = z.lazy(() => z.union([
    ParagraphSchema,
    HeadingSchema,
    BulletListSchema,
    BlockquoteSchema,
    ArticleImageNode,
    ArticleVideoNode,
    ArticleEmbedNode,
]));
export const ArticleBodySchema = z
    .object({
    type: z.literal('doc'),
    content: z.array(BlockSchema).default([]),
})
    .strict();
/** Documento vacío: es lo que tiene una nota recién creada. */
export const EMPTY_ARTICLE_BODY = { type: 'doc', content: [] };
/**
 * ¿Tiene contenido de verdad? Un `doc` con un párrafo vacío es lo que deja el editor cuando
 * se toca y no se escribe: para publicar no cuenta.
 */
export function articleBodyIsEmpty(body) {
    return !body.content.some(nodoTieneContenido);
}
/**
 * Recorre el árbol sin distinguir el tipo de nodo. Se tipa flojo a propósito: la unión de
 * nodos ya la garantizó Zod, y un `switch` sobre doce variantes solo para bajar un nivel es
 * ruido — lo que importa es "¿hay una imagen, un video, una publicación o un texto que no esté en
 * blanco?".
 */
function nodoTieneContenido(nodo) {
    const n = nodo;
    if (n.type === 'image' || n.type === 'video' || n.type === 'embed')
        return true;
    if (n.type === 'text')
        return (n.text ?? '').trim().length > 0;
    return (n.content ?? []).some(nodoTieneContenido);
}
/**
 * Ids de `Media` referenciados por el cuerpo: sirven para enlazar las fotos a la nota. Recorre
 * también las fotos que quedaron dentro de una lista o una cita.
 */
export function articleBodyMediaIds(body) {
    const ids = [];
    const recorrer = (nodo) => {
        const n = nodo;
        if (n.type === 'image' && n.attrs?.mediaId)
            ids.push(n.attrs.mediaId);
        (n.content ?? []).forEach(recorrer);
    };
    body.content.forEach(recorrer);
    return [...new Set(ids)];
}
/**
 * El cuerpo con las imágenes resueltas: le cuelga `attrs.imagen` a cada nodo `image` a partir de
 * su `mediaId`. Es el paso de **lectura**, y va en los dos caminos que muestran una nota —el
 * sitio público y el editor del panel, que al reabrir un borrador solo tiene el id—.
 *
 * Un `mediaId` sin fila (la foto se borró del bucket, o quedó huérfana) da `imagen: null` y no
 * revienta la nota: el renderer no dibuja nada y el resto del texto se lee igual.
 */
export function articleBodyConImagenes(body, imagenes) {
    return mapearNodos(body, (attrs) => ({
        ...attrs,
        imagen: imagenes.get(attrs.mediaId) ?? null,
    }));
}
/**
 * El cuerpo tal como se guarda: sin `attrs.imagen`. Es el paso de **escritura**, y es lo que hace
 * que ninguna URL entre a la base aunque el editor devuelva la imagen que le mandamos para
 * dibujar. Se aplica en el servidor y no en el cliente a propósito: la garantía no puede depender
 * de que el panel se acuerde.
 */
export function articleBodySinImagenes(body) {
    return mapearNodos(body, ({ imagen: _descartada, ...attrs }) => attrs);
}
/**
 * Reconstruye el árbol aplicando `fn` a los `attrs` de cada nodo `image`, a cualquier
 * profundidad: una foto puede estar dentro de una lista o de una cita. Se tipa flojo por dentro
 * por el mismo motivo que `nodoTieneContenido` — la unión de nodos ya la garantizó Zod, y un
 * `switch` sobre doce variantes para bajar un nivel es ruido.
 */
function mapearNodos(body, fn) {
    const recorrer = (nodo) => {
        const n = nodo;
        if (n.type === 'image' && n.attrs)
            return { ...n, attrs: fn(n.attrs) };
        if (!n.content)
            return n;
        return { ...n, content: n.content.map(recorrer) };
    };
    return { ...body, content: body.content.map(recorrer) };
}
